import classnames from "classnames";
import { kindOfIconButton } from "../../consts/Button/iconConstButton";
import { buttonVariant } from "../../consts/Button/variantConstButton";
import { IconModelEnum } from "../../enum/Button/iconButton.model";
import { VarianButtonEnum } from "../../enum/Button/variantButton.model";
import { Icon, Props, Variant } from "./types";

export const Button = ({
  ariaLabel,
  ariaDescribedBy,
  ariaExpanded,
  ariaPressed,
  ariaControls,
  text,
  icon,
  variant,
  bgColor,
  textColor,
  border,
  rounded = true,
  textTransform = true,
  fullWidth = false,
  onClickEvent,
  onKeyDownEvent,
  customClassNames,
  customClassNamesSpan,
  disclaimer,
  type = "button",
  onMobileView,
  disabled,
  role,
  tabIndex
}: Props) => {
  const variantClassName = (
    element: VarianButtonEnum,
    objectVariant: Variant,
  ) => objectVariant[element];
  const iconOfButton = (element: IconModelEnum, objectIcon: Icon) =>
    objectIcon[element];

  // Generate accessible label if none provided
  const getAccessibleLabel = () => {
    if (ariaLabel) return ariaLabel;
    if (typeof text === "string") return text;
    if (icon) return `${icon} button`;
    return "Button";
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (onKeyDownEvent) {
      onKeyDownEvent(event);
    }
    
    // Handle Enter and Space keys for button activation
    if ((event.key === "Enter" || event.key === " ") && !disabled) {
      event.preventDefault();
      if (onClickEvent) {
        onClickEvent(event as unknown as React.MouseEvent<HTMLButtonElement>);
      }
    }
  };

  return (
    <button
      aria-label={getAccessibleLabel()}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-pressed={ariaPressed}
      aria-controls={ariaControls}
      disabled={disabled}
      type={type}
      onClick={onClickEvent}
      onKeyDown={handleKeyDown}
      role={role}
      tabIndex={tabIndex}
      className={classnames(
        variantClassName(variant, buttonVariant),
        { "w-full ": fullWidth, "rounded-3xl": rounded, border: border, "!cursor-not-allowed": disabled },
        "flex justify-center items-center w-max outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        customClassNames,
        bgColor
      )}
    >
      {icon && <span>{iconOfButton(icon, kindOfIconButton)}</span>}
      {text && (
        <span
          className={classnames(
            { "ml-2": icon,
              capitalize: !textTransform,
              "uppercase": !disclaimer,
              "text-small text-gray-suva hover:underline font-normal": disclaimer,
              "hidden sm:inline" : onMobileView
            },
            textColor,
            customClassNamesSpan,
          )}
        >
          {text}
        </span>
      )}
    </button>
  );
};